import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  UsersIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ArrowTrendingUpIcon,
  VideoCameraIcon,
  PlayIcon,
  StopIcon,
  TrophyIcon,
  AcademicCapIcon,
  ArrowDownTrayIcon,
  ShieldCheckIcon,
  ServerStackIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  getAnalyticsData,
  getAllApplications,
  getCachedApplications,
  onAuthStateChange,
  getClassSettings,
  getDashboardAttendanceStats,
  setLiveBroadcastState,
  updateClassSettings
} from '../services/firebaseService';
import {
  subscribeToExamAttempts,
  exportConsolidatedMeritListToExcel,
  buildConsolidatedMeritList
} from '../services/examService';
import { AnalyticsData, Application, ClassSettings } from '../types';
import { formatDate } from '../utils/helpers';
import EmailTestComponent from '../components/admin/EmailTestComponent';
import { getEmailServiceStatus } from '../services/emailService';
import AdminHeader from '../components/admin/AdminHeader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const AdminDashboard: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [allApplications, setAllApplications] = useState<Application[]>([]);
  const [emailStats, setEmailStats] = useState<any>(null);
  const [classSettings, setClassSettings] = useState<ClassSettings | null>(null);
  const [videoStats, setVideoStats] = useState({
    totalVideos: 0,
    totalAttendees: 0,
    totalHours: '0.0',
    totalSessions: 0
  });
  const [loading, setLoading] = useState(true);
  const [dashboardSearch, setDashboardSearch] = useState('');
  const navigate = useNavigate();

  // Quick Stream Controller States
  const [quickStreamUrl, setQuickStreamUrl] = useState('');
  const [quickLiveLabel, setQuickLiveLabel] = useState('Second Phase Online Training — Live Session');
  const [isUpdatingStream, setIsUpdatingStream] = useState(false);
  const [streamActionMessage, setStreamActionMessage] = useState<string | null>(null);
  const [liveAttempts, setLiveAttempts] = useState<any[]>([]);

  // Subscribe to live attempts for real-time exam & merit data
  useEffect(() => {
    const unsub = subscribeToExamAttempts('final_exam_set2', (list) => {
      setLiveAttempts(list);
    });
    return () => unsub();
  }, []);

  const consolidatedList = useMemo(() => {
    return buildConsolidatedMeritList(liveAttempts);
  }, [liveAttempts]);

  const handleToggleLiveBroadcast = async () => {
    if (!classSettings) return;
    setIsUpdatingStream(true);
    setStreamActionMessage(null);
    try {
      const nextLiveState = !classSettings.isLiveNow;
      await setLiveBroadcastState({
        isLiveNow: nextLiveState,
        youtubeUrl: quickStreamUrl.trim() || classSettings.youtubeUrl || '',
        liveLabel: quickLiveLabel || classSettings.liveLabel || 'Second Phase Online Training — Live Session'
      });
      setClassSettings(prev => prev ? {
        ...prev,
        isLiveNow: nextLiveState,
        youtubeUrl: quickStreamUrl.trim() || prev.youtubeUrl || '',
        liveLabel: quickLiveLabel || prev.liveLabel || 'Second Phase Online Training — Live Session'
      } : null);
      setStreamActionMessage(nextLiveState ? '🔴 Live broadcast is now ACTIVE for students!' : '⏹️ Broadcast ended. Classroom is now offline.');
      setTimeout(() => setStreamActionMessage(null), 5000);
    } catch (err: any) {
      alert('Error updating broadcast state: ' + (err.message || 'Unknown error'));
    } finally {
      setIsUpdatingStream(false);
    }
  };

  const handleUpdateStreamUrl = async () => {
    if (!quickStreamUrl.trim()) {
      alert('Please enter a valid YouTube video or live URL');
      return;
    }
    setIsUpdatingStream(true);
    setStreamActionMessage(null);
    try {
      await updateClassSettings({
        youtubeUrl: quickStreamUrl.trim(),
        liveLabel: quickLiveLabel.trim() || 'Second Phase Online Training — Live Session'
      });
      setClassSettings(prev => prev ? {
        ...prev,
        youtubeUrl: quickStreamUrl.trim(),
        liveLabel: quickLiveLabel.trim() || 'Second Phase Online Training — Live Session'
      } : null);
      setStreamActionMessage('✅ YouTube Stream URL saved successfully!');
      setTimeout(() => setStreamActionMessage(null), 4000);
    } catch (err: any) {
      alert('Error saving stream URL: ' + (err.message || 'Unknown error'));
    } finally {
      setIsUpdatingStream(false);
    }
  };

  useEffect(() => {
    // 1. Instant Cache Hydration: If data was cached in memory or session, render immediately (< 5ms)
    const cachedApps = getCachedApplications();
    if (cachedApps && cachedApps.length > 0) {
      setAllApplications(cachedApps);
      getAnalyticsData(cachedApps).then(analytics => {
        setAnalyticsData(analytics);
        setLoading(false);
      }).catch(() => {});
    }

    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin/login');
      return;
    }

    // 2. Fetch / Revalidate data with optimized capped queries
    const fetchData = async () => {
      try {
        const [allApps, emailStatus, fetchedClassSettings, attendanceStats] = await Promise.all([
          getAllApplications().catch(() => []),
          getEmailServiceStatus().catch(() => ({ usage: null })),
          getClassSettings().catch(() => null),
          getDashboardAttendanceStats().catch(() => ({
            totalVideos: 0,
            totalAttendees: 0,
            totalHours: '0.0',
            totalSessions: 0,
            records: []
          }))
        ]);

        const analytics = await getAnalyticsData(allApps).catch(() => null);

        if (analytics) {
          setAnalyticsData(analytics);
        } else {
          // Reliable fallback analytics if parsing had issues
          setAnalyticsData({
            totalApplications: allApps.length,
            pendingApplications: allApps.filter(a => a.status === 'submitted').length,
            hrApplications: 0,
            underReviewApplications: 0,
            onHoldApplications: 0,
            clarificationApplications: 0,
            approvedApplications: allApps.length,
            rejectedApplications: 0,
            applicationsThisMonth: allApps.length,
            applicationsThisWeek: allApps.length,
            applicationsByDistrict: { Odisha: allApps.length },
            applicationsByMonth: {},
          });
        }

        setAllApplications(allApps);
        setEmailStats(emailStatus?.usage || null);
        setClassSettings(fetchedClassSettings);
        if (fetchedClassSettings?.youtubeUrl) {
          setQuickStreamUrl(fetchedClassSettings.youtubeUrl);
        }
        if (fetchedClassSettings?.liveLabel) {
          setQuickLiveLabel(fetchedClassSettings.liveLabel);
        }
        setVideoStats({
          totalVideos: attendanceStats.totalVideos,
          totalAttendees: attendanceStats.totalAttendees,
          totalHours: attendanceStats.totalHours,
          totalSessions: attendanceStats.totalSessions
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    const unsubscribe = onAuthStateChange((user) => {
      if (!user) {
        const storedAdmin = localStorage.getItem('adminUser');
        if (!storedAdmin) {
          navigate('/admin/login');
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleExportExcel = async () => {
    try {
      const applications = allApplications.length > 0 ? allApplications : await getAllApplications();
      const exportData = applications.map((app: Application) => ({
        'Registration ID': app.registrationNumber || app.id,
        'Name': `${app.firstName || ''} ${app.lastName || ''}`.trim(),
        'Email': app.emailId,
        'Mobile': app.mobileNumber,
        'District': app.address?.district || 'N/A',
        'Block': app.address?.block || 'N/A',
        'Status': app.status,
        'Submitted Date': formatDate(app.submittedAt),
        'Reviewed Date': app.reviewedAt ? formatDate(app.reviewedAt) : 'N/A'
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Applications');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      saveAs(blob, `kreed-odisha-applications-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data. Please try again.');
    }
  };

  // Search filtered results on dashboard
  const searchResults = useMemo(() => {
    if (!dashboardSearch.trim()) return [];
    const query = dashboardSearch.toLowerCase();
    return allApplications.filter(app =>
      `${app.firstName || ''} ${app.lastName || ''}`.toLowerCase().includes(query) ||
      (app.registrationNumber || '').toLowerCase().includes(query) ||
      (app.id || '').toLowerCase().includes(query) ||
      (app.mobileNumber || '').includes(query) ||
      (app.emailId || '').toLowerCase().includes(query)
    ).slice(0, 6);
  }, [dashboardSearch, allApplications]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
          <div className="mt-4 text-center">
            <p className="text-sm font-bold text-slate-800">Loading Admin Dashboard...</p>
            <p className="text-xs text-slate-500">Syncing telemetry and registrations</p>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-slate-200 max-w-md text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-900 mb-1">Failed to Load Dashboard</h2>
          <p className="text-xs text-slate-500 mb-5">There was a problem communicating with the database.</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-2.5 px-4 rounded-xl text-xs transition"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Pending review applications
  const pendingApps = allApplications.filter(app => app.status === 'submitted');
  const recentProcessedApps = allApplications.filter(app => app.status !== 'submitted').slice(0, 5);

  // Status chart configurations
  const statusChartData = {
    labels: ['New', 'Verified', 'Rejected', 'Under Review', 'On Hold'],
    datasets: [
      {
        data: [
          analyticsData.pendingApplications,
          analyticsData.approvedApplications,
          analyticsData.rejectedApplications,
          analyticsData.underReviewApplications,
          analyticsData.onHoldApplications
        ],
        backgroundColor: ['#3b82f6', '#10B981', '#EF4444', '#f59e0b', '#6b7280'],
        borderWidth: 0,
        hoverOffset: 6
      }
    ]
  };

  const monthlyChartData = {
    labels: Object.keys(analyticsData.applicationsByMonth).slice(-6),
    datasets: [
      {
        label: 'Registrations',
        data: Object.values(analyticsData.applicationsByMonth).slice(-6),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.12)',
        tension: 0.35,
        fill: true,
        pointBackgroundColor: '#2563eb',
        pointRadius: 4
      }
    ]
  };

  const districtChartData = {
    labels: Object.keys(analyticsData.applicationsByDistrict).slice(0, 10),
    datasets: [
      {
        label: 'Registrations',
        data: Object.values(analyticsData.applicationsByDistrict).slice(0, 10),
        backgroundColor: 'rgba(99, 102, 241, 0.85)',
        borderColor: 'rgb(79, 70, 229)',
        borderWidth: 1,
        borderRadius: 8
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          font: { size: 11, weight: 'bold' as const }
        }
      }
    }
  };

  const totalCount = analyticsData.totalApplications || 1;
  const approvedPct = Math.round((analyticsData.approvedApplications / totalCount) * 100);
  const pendingPct = Math.round((analyticsData.pendingApplications / totalCount) * 100);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Unified Admin Header */}
      <AdminHeader
        activeNav="dashboard"
        title="Admin Dashboard"
        subtitle="Command Center & Live Telemetry"
        onExportExcel={handleExportExcel}
        isLiveNow={classSettings?.isLiveNow}
      />

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto py-5 sm:py-8 px-3 sm:px-6 lg:px-8 pb-24 lg:pb-12 space-y-6">

        {/* Fast Applicant Search & Jump Banner */}
        <div className="bg-white rounded-3xl p-4 sm:p-5 shadow-sm border border-slate-200/80">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={dashboardSearch}
              onChange={(e) => setDashboardSearch(e.target.value)}
              placeholder="Quick search applicant by name, registration ID (e.g. KOP/...), phone, or email..."
              className="w-full pl-11 pr-10 py-3 rounded-2xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 text-sm font-medium transition"
            />
            {dashboardSearch && (
              <button
                type="button"
                onClick={() => setDashboardSearch('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 hover:text-slate-600 bg-slate-200 rounded-full w-5 h-5 flex items-center justify-center"
              >
                ✕
              </button>
            )}
          </div>

          {/* Instant Search Results Dropdown/Box */}
          {dashboardSearch.trim() && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Search Results ({searchResults.length})
                </span>
                <button
                  onClick={() => navigate(`/admin/applications?q=${encodeURIComponent(dashboardSearch)}`)}
                  className="text-xs font-bold text-primary-600 hover:text-primary-800"
                >
                  View full search in Registrations →
                </button>
              </div>

              {searchResults.length === 0 ? (
                <p className="text-xs text-slate-500 py-2 italic text-center">No matching registrations found for "{dashboardSearch}"</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                  {searchResults.map(app => (
                    <div
                      key={app.id}
                      onClick={() => navigate(`/admin/applications/${app.id}`)}
                      className="p-3 bg-slate-50 hover:bg-primary-50/60 border border-slate-200/80 hover:border-primary-300 rounded-2xl cursor-pointer transition flex items-center justify-between group"
                    >
                      <div className="min-w-0 pr-2">
                        <p className="text-xs font-bold text-slate-900 group-hover:text-primary-700 truncate">
                          {app.firstName} {app.lastName}
                        </p>
                        <p className="text-[10px] text-slate-500 font-mono truncate">
                          {app.registrationNumber || app.id}
                        </p>
                        <span className={`inline-block mt-1 text-[9px] font-bold px-1.5 py-0.2 rounded-full uppercase ${app.status === 'verified' ? 'bg-emerald-100 text-emerald-800' :
                            app.status === 'rejected' ? 'bg-rose-100 text-rose-800' :
                              'bg-amber-100 text-amber-800'
                          }`}>
                          {app.status}
                        </span>
                      </div>
                      <ChevronRightIcon className="w-4 h-4 text-slate-400 group-hover:text-primary-600 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Active Database & Telemetry Health Bar ── */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border border-indigo-500/30 rounded-3xl p-4 sm:p-5 shadow-lg text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 text-indigo-400 border border-indigo-500/40 flex items-center justify-center shrink-0">
                <ServerStackIcon className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-black uppercase tracking-wider text-white">Primary Cloud Cluster:</span>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center space-x-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    <span>okreedba (Blaze Active)</span>
                  </span>
                </div>
                <p className="text-xs text-indigo-200/80 mt-0.5">
                  Production database linked to Google Cloud Blaze Plan with active credits &amp; pay-as-you-go scaling.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="px-3 py-1.5 rounded-xl bg-white/10 border border-white/10 text-slate-300 font-mono flex items-center space-x-1.5">
                <UsersIcon className="w-3.5 h-3.5 text-indigo-400" />
                <span>Roster: <strong className="text-white">745 Candidates</strong></span>
              </span>
              <span className="px-3 py-1.5 rounded-xl bg-white/10 border border-white/10 text-slate-300 font-mono flex items-center space-x-1.5">
                <ShieldCheckIcon className="w-3.5 h-3.5 text-emerald-400" />
                <span>Attendance Logs: <strong className="text-white">1,639+</strong></span>
              </span>
              <span className="px-3 py-1.5 rounded-xl bg-white/10 border border-white/10 text-slate-300 font-mono flex items-center space-x-1.5">
                <BoltIcon className="w-3.5 h-3.5 text-amber-400" />
                <span>Zero-Latency Auth</span>
              </span>
            </div>
          </div>
        </div>

        {/* ── Live Classroom Sessions & Video Hub ── */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white rounded-3xl p-5 sm:p-7 shadow-xl border border-slate-800">
          <div className="absolute top-0 right-0 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-2.5 max-w-2xl">
              <div className="flex items-center space-x-2 flex-wrap gap-y-1.5">
                <span className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider ${classSettings?.isLiveNow
                    ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 shadow-sm shadow-rose-900/50'
                    : 'bg-white/10 text-slate-300 border border-white/10'
                  }`}>
                  <span className={`w-2 h-2 rounded-full ${classSettings?.isLiveNow ? 'bg-rose-500 animate-ping' : 'bg-slate-400'}`} />
                  <span>{classSettings?.isLiveNow ? 'Broadcast Live Now' : 'Classroom Offline'}</span>
                </span>

                <span className="text-[11px] text-indigo-300 font-bold bg-indigo-500/20 px-2.5 py-1 rounded-full border border-indigo-500/30">
                  YouTube Video Segments
                </span>
              </div>

              <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white">
                Live Classroom Hub &amp; Attendance Analytics
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Isolate student watch time, custom classroom chat, and real-time audit logs across training sessions.
              </p>

              {classSettings?.isLiveNow && (
                <div className="pt-1 flex items-center space-x-2 text-xs text-rose-200 bg-rose-950/60 p-2.5 rounded-xl border border-rose-800/50">
                  <span className="truncate">Streaming: <strong className="text-white">{classSettings.liveLabel}</strong></span>
                  {classSettings.activeInstructorName && (
                    <>
                      <span>&bull;</span>
                      <span className="truncate">Faculty: <strong className="text-white">{classSettings.activeInstructorName}</strong></span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Quick Metrics & Actions on Hub */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2.5 sm:p-3 border border-white/10 text-center min-w-[80px]">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-300">Videos</p>
                  <p className="text-lg sm:text-2xl font-black text-white">{videoStats.totalVideos}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2.5 sm:p-3 border border-white/10 text-center min-w-[80px]">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-300">Attendees</p>
                  <p className="text-lg sm:text-2xl font-black text-white">{videoStats.totalAttendees}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2.5 sm:p-3 border border-white/10 text-center min-w-[80px]">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-300">Watch</p>
                  <p className="text-lg sm:text-2xl font-black text-white">{videoStats.totalHours}h</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/admin/attendance?mode=videos')}
                  className="px-4 py-2.5 bg-rose-600 hover:bg-rose-700 active:scale-95 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center space-x-1.5 cursor-pointer"
                >
                  <span>📹 Video Segments &amp; Chats</span>
                  <span>→</span>
                </button>
                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/attendance?mode=users')}
                    className="px-2 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-[11px] font-semibold transition text-center cursor-pointer"
                  >
                    👥 Students
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/admin/attendance?mode=audit')}
                    className="px-2 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-[11px] font-semibold transition text-center cursor-pointer"
                  >
                    📜 Audit
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/admin/settings?tab=class-stream')}
                    className="px-2 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-[11px] font-semibold transition text-center cursor-pointer"
                    title="Stream & Schedules"
                  >
                    ⚙️ Setup
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stream & Broadcast Controls */}
          <div className="mt-5 pt-4 border-t border-white/10 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-white/5 p-3.5 rounded-2xl">
            <div className="flex-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative flex-1">
                <VideoCameraIcon className="w-4 h-4 text-indigo-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Paste YouTube Live / Class Video URL (e.g. https://youtube.com/live/...)"
                  value={quickStreamUrl}
                  onChange={(e) => setQuickStreamUrl(e.target.value)}
                  className="w-full bg-black/40 border border-white/15 rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-500 font-mono"
                />
              </div>

              <button
                type="button"
                onClick={handleUpdateStreamUrl}
                disabled={isUpdatingStream}
                className="px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs transition disabled:opacity-50 shrink-0 cursor-pointer"
              >
                Save URL
              </button>
            </div>

            <div className="flex items-center space-x-2 shrink-0">
              <button
                type="button"
                onClick={handleToggleLiveBroadcast}
                disabled={isUpdatingStream}
                className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center space-x-1.5 shadow-lg cursor-pointer ${
                  classSettings?.isLiveNow
                    ? 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white shadow-rose-900/50'
                    : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 shadow-emerald-900/50'
                }`}
              >
                {classSettings?.isLiveNow ? (
                  <>
                    <StopIcon className="w-4 h-4 text-white" />
                    <span>Stop Broadcast</span>
                  </>
                ) : (
                  <>
                    <PlayIcon className="w-4 h-4 text-slate-950" />
                    <span>Go Live Now</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate('/admin/settings?tab=class-stream')}
                className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition cursor-pointer"
                title="Full Class Schedule Settings"
              >
                📅 Schedules
              </button>
            </div>
          </div>

          {streamActionMessage && (
            <div className="mt-2.5 p-2.5 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs font-bold animate-fade-in flex items-center space-x-2">
              <CheckCircleIcon className="w-4 h-4 shrink-0" />
              <span>{streamActionMessage}</span>
            </div>
          )}
        </div>

        {/* ── Key Metrics Cards (Modern SaaS Polish & Mobile 2x2 Grid) ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {/* Total Registrations */}
          <div
            onClick={() => navigate('/admin/applications')}
            className="bg-white rounded-3xl p-4 sm:p-5 shadow-sm border border-slate-200/80 hover:shadow-md hover:border-blue-300 cursor-pointer transition group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <UsersIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-blue-100/80 text-blue-800">
                Total
              </span>
            </div>
            <p className="text-xs font-semibold text-slate-500">Total Registrations</p>
            <p className="text-2xl sm:text-3xl font-black text-slate-900 mt-0.5">
              {analyticsData.totalApplications}
            </p>
            <div className="mt-2 text-[11px] font-bold text-blue-600 flex items-center space-x-1">
              <span>View all records</span>
              <span>→</span>
            </div>
          </div>

          {/* Pending Review */}
          <div
            onClick={() => navigate('/admin/applications')}
            className="bg-white rounded-3xl p-4 sm:p-5 shadow-sm border border-slate-200/80 hover:shadow-md hover:border-amber-300 cursor-pointer transition group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <ClockIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {pendingPct}% queue
              </span>
            </div>
            <p className="text-xs font-semibold text-slate-500">Pending Review</p>
            <p className="text-2xl sm:text-3xl font-black text-amber-600 mt-0.5">
              {analyticsData.pendingApplications}
            </p>
            <div className="mt-2 text-[11px] font-bold text-amber-700 flex items-center space-x-1">
              <span>Action required</span>
              <span>→</span>
            </div>
          </div>

          {/* Approved / Verified */}
          <div
            onClick={() => navigate('/admin/applications')}
            className="bg-white rounded-3xl p-4 sm:p-5 shadow-sm border border-slate-200/80 hover:shadow-md hover:border-emerald-300 cursor-pointer transition group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <CheckCircleIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                {approvedPct}% approved
              </span>
            </div>
            <p className="text-xs font-semibold text-slate-500">Approved Candidates</p>
            <p className="text-2xl sm:text-3xl font-black text-emerald-600 mt-0.5">
              {analyticsData.approvedApplications}
            </p>
            <div className="mt-2 text-[11px] font-bold text-emerald-700 flex items-center space-x-1">
              <span>Letters ready</span>
              <span>→</span>
            </div>
          </div>

          {/* Rejected / Disqualified */}
          <div
            onClick={() => navigate('/admin/applications')}
            className="bg-white rounded-3xl p-4 sm:p-5 shadow-sm border border-slate-200/80 hover:shadow-md hover:border-rose-300 cursor-pointer transition group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <XCircleIcon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">
                Disqualified
              </span>
            </div>
            <p className="text-xs font-semibold text-slate-500">Rejected</p>
            <p className="text-2xl sm:text-3xl font-black text-rose-600 mt-0.5">
              {analyticsData.rejectedApplications}
            </p>
            <div className="mt-2 text-[11px] font-bold text-rose-700 flex items-center space-x-1">
              <span>Audited</span>
              <span>→</span>
            </div>
          </div>
        </div>

        {/* ── Phase 2 Assessment & Consolidated Merit Hub Card ── */}
        <div className="bg-gradient-to-br from-slate-900 via-emerald-950/70 to-slate-900 border border-emerald-500/40 rounded-3xl p-5 sm:p-7 shadow-xl text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center space-x-2 flex-wrap gap-y-1.5">
                <span className="px-3 py-1 rounded-full text-xs font-black tracking-wider bg-emerald-500 text-slate-950 uppercase shadow-md flex items-center space-x-1.5">
                  <TrophyIcon className="w-4 h-4" />
                  <span>PHASE 2 EVALUATION &amp; MERIT HUB</span>
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-emerald-200 border border-emerald-500/30">
                  Set 1 + Set 2 Unified
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                  Official Merit Roster
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                Consolidated Candidate Merit List &amp; Scorecards
              </h2>
              <p className="text-xs sm:text-sm text-emerald-100/80 max-w-2xl leading-relaxed">
                Consolidated evaluation roster combining Assessment Set 1 (18 Oct) and Assessment Set 2. Best performance marks, merit ranks, qualification status, and certificate issuance records.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 shrink-0">
              <button
                type="button"
                onClick={() => exportConsolidatedMeritListToExcel(liveAttempts)}
                className="px-4 py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg transition flex items-center justify-center space-x-2 cursor-pointer active:scale-95"
              >
                <ArrowDownTrayIcon className="w-4 h-4 text-slate-950" />
                <span>Export Merit List (.xlsx)</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/exams')}
                className="px-4 py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase tracking-wider transition flex items-center justify-center space-x-2 border border-white/15 cursor-pointer"
              >
                <AcademicCapIcon className="w-4 h-4 text-emerald-400" />
                <span>Open Exam Supervision Console →</span>
              </button>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-black/30 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] uppercase font-bold text-slate-400">Total Evaluated</p>
              <p className="text-xl font-black text-white mt-0.5">{consolidatedList.length} Candidates</p>
            </div>
            <div className="bg-black/30 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] uppercase font-bold text-slate-400">Total Qualified (&gt;=50%)</p>
              <p className="text-xl font-black text-emerald-400 mt-0.5">
                {consolidatedList.filter(c => c.isEligibleForCertificate).length} ({consolidatedList.length > 0 ? Math.round((consolidatedList.filter(c => c.isEligibleForCertificate).length / consolidatedList.length) * 100) : 0}%)
              </p>
            </div>
            <div className="bg-black/30 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] uppercase font-bold text-slate-400">Distinctions (&gt;=80%)</p>
              <p className="text-xl font-black text-amber-400 mt-0.5">
                {consolidatedList.filter(c => c.bestPct >= 80).length} Candidates
              </p>
            </div>
            <div className="bg-black/30 rounded-xl p-3 border border-white/5">
              <p className="text-[10px] uppercase font-bold text-slate-400">Average Best Score</p>
              <p className="text-xl font-black text-indigo-400 mt-0.5">
                {consolidatedList.length > 0 ? (consolidatedList.reduce((acc, c) => acc + c.bestScore, 0) / consolidatedList.length).toFixed(1) : 0} / 30
              </p>
            </div>
          </div>
        </div>

        {/* Email Limit Warning if triggered */}
        {emailStats && emailStats.dailyCount >= emailStats.warning && (
          <div className={`p-4 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${emailStats.dailyCount >= emailStats.limit ? 'bg-red-50 border-red-200 text-red-900' : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}>
            <div className="flex items-center space-x-3">
              <ExclamationTriangleIcon className="w-6 h-6 flex-shrink-0 text-amber-600" />
              <div>
                <p className="font-bold text-xs sm:text-sm">
                  {emailStats.dailyCount >= emailStats.limit ? 'Daily Email Limit Reached!' : 'Approaching Daily Email Limit'}
                </p>
                <p className="text-xs">
                  Usage: {emailStats.dailyCount} / {emailStats.limit} sent today.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/settings')}
              className="text-xs font-bold px-3 py-1.5 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 transition shadow-sm"
            >
              Configure SMTP
            </button>
          </div>
        )}

        {/* ── Charts Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          {/* Status Breakdown */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm sm:text-base font-bold text-slate-900">Registration Status Breakdown</h3>
                <p className="text-xs text-slate-500">Overall distribution across all stages</p>
              </div>
              <span className="p-2 bg-slate-100 rounded-xl text-slate-600">
                <ChartBarIcon className="w-4 h-4" />
              </span>
            </div>
            <div className="h-60 sm:h-72">
              <Doughnut data={statusChartData} options={chartOptions} />
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm sm:text-base font-bold text-slate-900">Monthly Registration Trend</h3>
                <p className="text-xs text-slate-500">Intake velocity across past cycles</p>
              </div>
              <span className="p-2 bg-slate-100 rounded-xl text-slate-600">
                <ArrowTrendingUpIcon className="w-4 h-4" />
              </span>
            </div>
            <div className="h-60 sm:h-72">
              <Line data={monthlyChartData} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* District Distribution Bar Chart */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900">Top 10 Districts by Registrations</h3>
              <p className="text-xs text-slate-500">Regional sports talent geographic distribution</p>
            </div>
            <span className="text-xs font-bold text-primary-600 bg-primary-50 px-2.5 py-1 rounded-xl border border-primary-200">
              Odisha State
            </span>
          </div>
          <div className="h-72 sm:h-80">
            <Bar data={districtChartData} options={chartOptions} />
          </div>
        </div>

        {/* ── Pending Review: Action Required ── */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <div className="flex items-center space-x-2">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                <h3 className="text-base font-bold text-slate-900">
                  New Registrations Pending Review ({pendingApps.length})
                </h3>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Immediate verification required for sports eligibility
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/applications')}
              className="text-xs font-bold text-primary-600 hover:text-primary-800 flex items-center space-x-1"
            >
              <span>View full queue</span>
              <span>→</span>
            </button>
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    District / Block
                  </th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pendingApps.slice(0, 5).map((app) => (
                  <tr key={app.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 rounded-xl bg-primary-100 text-primary-700 font-bold text-xs flex items-center justify-center uppercase">
                          {app.firstName?.[0] || 'C'}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-900">
                            {app.firstName} {app.lastName}
                          </p>
                          <p className="text-[11px] text-slate-500 font-mono">
                            {app.registrationNumber || app.id}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-xs font-semibold text-slate-800">{app.address?.district || 'N/A'}</p>
                      <p className="text-[11px] text-slate-400">{app.address?.block || '—'}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-800">
                        New Submission
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 font-medium">
                      {formatDate(app.submittedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => navigate(`/admin/applications/${app.id}`)}
                        className="bg-primary-50 hover:bg-primary-100 text-primary-700 font-bold px-3 py-1.5 rounded-xl text-xs transition inline-flex items-center space-x-1"
                      >
                        <span>Review</span>
                        <ChevronRightIcon className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View (Optimized for Phones) */}
          <div className="sm:hidden divide-y divide-slate-100">
            {pendingApps.slice(0, 5).map((app) => (
              <div key={app.id} className="p-4 space-y-2.5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2.5 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-primary-100 text-primary-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                      {app.firstName?.[0] || 'C'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-900 truncate">
                        {app.firstName} {app.lastName}
                      </p>
                      <p className="text-[10px] text-slate-500 font-mono truncate">
                        {app.registrationNumber || app.id}
                      </p>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-amber-100 text-amber-800 flex-shrink-0">
                    Pending
                  </span>
                </div>

                <div className="flex items-center justify-between text-[11px] text-slate-500 font-medium pt-1">
                  <span>📍 {app.address?.district || 'N/A'}</span>
                  <span>📅 {formatDate(app.submittedAt)}</span>
                </div>

                <button
                  type="button"
                  onClick={() => navigate(`/admin/applications/${app.id}`)}
                  className="w-full bg-primary-600 hover:bg-primary-700 active:scale-98 text-white font-bold py-2 px-3 rounded-xl text-xs transition flex items-center justify-center space-x-1.5 shadow-sm"
                >
                  <EyeIcon className="w-4 h-4" />
                  <span>Review Application</span>
                </button>
              </div>
            ))}
          </div>

          {pendingApps.length === 0 && (
            <div className="p-8 text-center bg-slate-50/50">
              <CheckCircleIcon className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <p className="text-xs font-bold text-slate-800">Queue is Clear!</p>
              <p className="text-[11px] text-slate-500">All submitted applications have been processed.</p>
            </div>
          )}
        </div>

        {/* ── Recently Processed Registrations ── */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-900">Recently Processed Candidates</h3>
            <button
              onClick={() => navigate('/admin/applications')}
              className="text-xs font-bold text-primary-600 hover:text-primary-800"
            >
              View all →
            </button>
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    District
                  </th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentProcessedApps.map((app) => (
                  <tr key={app.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-xs font-bold text-slate-900">
                        {app.firstName} {app.lastName}
                      </p>
                      <p className="text-[11px] text-slate-500 font-mono">
                        {app.registrationNumber || app.id}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-700">
                      {app.address?.district || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-0.5 text-[10px] font-bold rounded-full ${app.status === 'verified' ? 'bg-emerald-100 text-emerald-800' :
                          app.status === 'rejected' ? 'bg-rose-100 text-rose-800' :
                            app.status === 'under_review' ? 'bg-blue-100 text-blue-800' :
                              'bg-slate-100 text-slate-700'
                        }`}>
                        {app.status.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">
                      {formatDate(app.submittedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => navigate(`/admin/applications/${app.id}`)}
                        className="text-primary-600 hover:text-primary-800 text-xs font-bold inline-flex items-center space-x-1"
                      >
                        <EyeIcon className="w-4 h-4" />
                        <span>Inspect</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards for Processed Candidates */}
          <div className="sm:hidden divide-y divide-slate-100">
            {recentProcessedApps.map((app) => (
              <div key={app.id} className="p-4 flex items-center justify-between">
                <div className="min-w-0 pr-3">
                  <p className="text-xs font-bold text-slate-900 truncate">
                    {app.firstName} {app.lastName}
                  </p>
                  <p className="text-[10px] text-slate-500 font-mono truncate">
                    {app.registrationNumber || app.id} &bull; {app.address?.district}
                  </p>
                  <span className={`inline-block mt-1 text-[9px] font-bold px-2 py-0.2 rounded-full ${app.status === 'verified' ? 'bg-emerald-100 text-emerald-800' :
                      app.status === 'rejected' ? 'bg-rose-100 text-rose-800' :
                        'bg-slate-100 text-slate-700'
                    }`}>
                    {app.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/applications/${app.id}`)}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl flex-shrink-0"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Email Testing Sandbox Widget */}
        <div className="pt-2">
          <EmailTestComponent />
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
